<template>
	<div class="animated fadeIn">
		<b-card class="card-border mt-4">
			<b-card-title><i class="fa fa-truck"></i> Dispatch</b-card-title>
			<b-card-sub-title>Manages the outgoing dispatches for all companies</b-card-sub-title>
			<div fluid class="px-2 mt-4">
				<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

				<!-- Filter  -->
				<b-row class="mt-2">
					<b-col sm="12" md="3" lg="3">
						<b-button v-b-popover.hover.right="'Toggle to show/hide filter options'" v-b-toggle.collapse-1
							class="filter">
							FILTER OPTIONS
						</b-button>
					</b-col>
					<b-col sm="12">
						<!-- Collapsible Filter Options -->
						<b-collapse id="collapse-1" class="mt-2">
							<b-card>
								<b-row no-gutters>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Date From" description="Reference to the Date Created">
											<b-form-datepicker name="Date From" v-model="filterBy.dateFrom" locale="en"
												reset-button label-reset-button="Clear"
												:date-format-options="dateFormatOptions"
												:date-disabled-fn="dateFromDisabled" v-validate="'required'" />
											<span v-show="errors.has('Date From')" class="help-block">
												{{ errors.first('Date From') }}
											</span>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Date To" description="Reference to the Date Created">
											<b-form-datepicker name="Date To" v-model="filterBy.dateTo" locale="en"
												reset-button label-reset-button="Clear"
												:date-format-options="dateFormatOptions"
												:date-disabled-fn="dateFromDisabled" v-validate="'required'" />
											<span v-show="errors.has('Date To')" class="help-block">
												{{ errors.first('Date To') }}
											</span>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Source Company">
											<v-select name="Source Company" class="style-chooser" label="text"
												placeholder=" - Please select - " :options="allSourceCompanyOptions"
												:reduce="(company) => company.value" v-model="filterBy.sourceCompany"
												v-validate="{ 'selectRequired': sourceIsRequired }">
												<template v-slot:no-options="{ search, searching }">
													<template v-if="searching">
														No results found for
														<em>
															<strong>{{ search }}</strong>
														</em>
													</template>
													<em :style="{ opacity: 0.5 }" v-else>
														Start typing to search for a company
													</em>
												</template>
											</v-select>
											<span v-show="errors.has('Source Company')" class="help-block">
												{{ errors.first('Source Company') }}
											</span>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Destination Company">
											<v-select name="Destination Company" class="style-chooser" label="text"
												placeholder=" - Please select - "
												:options="allDestinationCompanyOptions"
												:reduce="(company) => company.value"
												v-model="filterBy.destinationCompany">
												<template v-slot:no-options="{ search, searching }">
													<template v-if="searching">
														No results found for
														<em>
															<strong>{{ search }}</strong>
														</em>
													</template>
													<em :style="{ opacity: 0.5 }" v-else>
														Start typing to search for a company
													</em>
												</template>
											</v-select>
											<span v-show="errors.has('Destination Company')" class="help-block">
												{{ errors.first('Destination Company') }}
											</span>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Status">
											<b-form-select v-model="filterBy.status" :options="statusOptions"
												class="mr-2" />
										</b-form-group>
									</b-col>

									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Plate No/CR/OR">
											<v-select class="style-chooser" label="text"
												placeholder=" - Please select - " :options="plateNoOptions"
												:reduce="(company) => company.value" v-model="filterBy.plateNo">
												<template v-slot:no-options="{ search, searching }">
													<template v-if="searching">
														No results found for
														<em>
															<strong>{{ search }}</strong>
														</em>
													</template>
													<em :style="{ opacity: 0.5 }" v-else>
														Start typing to search for a company
													</em>
												</template>
											</v-select>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Asset Code">
											<b-form-input id="assetCode" name="Asset Code" type="search" class="numFont"
												v-model="filterBy.assetCode"
												v-validate="{ regex: /^([a-zA-Z0-9\-])*$/ }" />
											<span v-show="errors.has('Asset Code')" class="help-block">
												{{ errors.first('Asset Code') }}
											</span>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Dispatch ID"
											description="NOTE: Input the exact Dispatch ID to search">
											<b-form-input id="dispatchId" name="Dispatch ID" type="search"
												class="numFont" v-model="filterBy.dispatchId"
												v-validate="getValidationParam(false, /^DS\d{13}$/)"
												placeholder="DSXXXXXXXXXXXXX" />
											<span v-show="errors.has('Dispatch ID')" class="help-block">
												{{ errors.first('Dispatch ID') }}
											</span>
										</b-form-group>
									</b-col>
								</b-row>
								<b-row no-gutters>
									<b-col sm="12">
										<b-button class="mr-1" variant="success" @click="onFilterRequest">
											Search
										</b-button>
										<b-button class="mr-1" variant="primary" @click="resetFilters">
											Reset
										</b-button>
									</b-col>
								</b-row>
							</b-card>
						</b-collapse>
					</b-col>
				</b-row>

				<!-- Select Actions and Items Per Page Options -->
				<b-row>
					<b-col sm="6" md="3" class="mt-4 mb-2">
						<b-dropdown text=" Select Actions " variant="dark" slot="append">
							<b-dropdown-item @click="addDispatch" v-show="!isViewer">
								Add Dispatch
							</b-dropdown-item>
							<b-dropdown-item>
								<json-excel :data="exportData" :fields="exportFields" type="xls"
									:name="fileName + '.xls'">
									Export Dispatches in Excel
								</json-excel>
							</b-dropdown-item>
							<b-dropdown-item>
								<json-excel :data="exportData" :fields="exportFields" type="csv"
									:name="fileName + '.csv'">
									Export Dispatches to CSV
								</json-excel>
							</b-dropdown-item>
						</b-dropdown>
					</b-col>
					<b-col sm="6" md="4" offset-md="5" class="mt-4 mb-2 text-md-right">
						<b-input-group prepend="Show" append="/ Page">
							<b-form-select :options="pageOptions" v-model="perPage" />
						</b-input-group>
					</b-col>
				</b-row>

				<b-table ref="dispatchesTable" show-empty striped hover :items="items" :fields="fields"
					:current-page="currentPage" :per-page="perPage" :filter="filter" :sort-by.sync="sortBy"
					:sort-desc.sync="sortDesc" :sort-direction="sortDirection" responsive>

					<template v-slot:cell(dispatchId)="row">
						<span class="numFont">
							{{ row.item.dispatchId }}
						</span>
					</template>

					<template v-slot:cell(dateDeployed)="row">{{
						row.item.dateDeployed
							? getDisplayDateTime(row.item.dateDeployed)
							: '-'
					}}</template>
					<template v-slot:cell(dateReceived)="row">{{
						row.item.dateReceived
							? getDisplayDateTime(row.item.dateReceived)
							: '-'
					}}</template>

					<template v-slot:cell(source)="row">
						<span class="company-display">
							{{ row.item.source.company }}
						</span>
						<div class="location-display">
							({{ row.item.source.storageLocation }})
						</div>
					</template>

					<template v-slot:cell(destination)="row">
						<span class="company-display">
							{{ row.item.destination.company }}
						</span>
						<div class="location-display">
							({{ row.item.destination.storageLocation }})
						</div>
					</template>

					<template v-slot:cell(trucker)="row">
						<span class="company-display">
							{{ row.item.transportation.plateNo }}
						</span>
						<div class="location-display">
							({{ row.item.driver.name }})
						</div>
					</template>

					<template v-slot:cell(dispatched)="row">
						<span class="numFont">
							{{ row.item['Dispatched'] }}
						</span>
					</template>

					<template v-slot:cell(received)="row">
						<span class="numFont">
							{{ row.item['Received'] }}
						</span>
					</template>

					<template v-slot:cell(quantity)="row">
						{{ row.item.assets.length }}
					</template>

					<template v-slot:cell(status)="row">
						<DispatchRowStatus :row="row" />
					</template>

					<template v-slot:cell(actions)="row">
						<DispatchRowActions :row="row" :isSuperAdmin="isSuperAdmin" :isManager="isManager"
							:isSupervisor="isSupervisor" :isViewer="isViewer" parentComponent="Dispatch"
							:allCompaniesObj="allCompaniesObj" />
					</template>

					<template slot="row-details" slot-scope="row">
						<DispatchDetailsView :row="row" :allUsersObj="allUsersObj" />
					</template>
				</b-table>

				<b-row>
					<b-col md="8" sm="12" class="my-1">
						<span class="total-display">Total: {{ totalRows ? totalRows.toLocaleString() : 0 }}</span>
					</b-col>
					<b-col md="4" sm="12" class="my-1">
						<b-pagination align="right" :total-rows="totalRows" :per-page="perPage" v-model="currentPage"
							class="my-0" />
					</b-col>
				</b-row>
			</div>
		</b-card>

		<!-- Modals here -->
		<DeployDispatch />
		<CancelDispatch />
		<ScannedAssetsView />
		<PrintDispatchSummary :allStorageLocationsObj="allStorageLocationsObj" :allUsersObj="allUsersObj" />
		<DispatchLocationView />
		<ProofReceiptImageView />
		<ProofReceiptImageLocationView />
		<DriversLicenseImageView />
	</div>
</template>

<script>
// Components
import DispatchRowStatus from '@/views/transactions/dispatch/DispatchRowStatus';
import DispatchRowActions from '@/views/transactions/dispatch/DispatchRowActions';
import DispatchDetailsView from '@/views/transactions/dispatch/DispatchDetailsView';
import DeployDispatch from '@/views/transactions/dispatch/DeployDispatch';
import CancelDispatch from '@/views/transactions/dispatch/CancelDispatch';
import ScannedAssetsView from '@/views/transactions/dispatch/ScannedAssetsView';
import PrintDispatchSummary from '@/views/transactions/dispatch/PrintDispatchSummary';
import DispatchLocationView from '@/views/transactions/dispatch/DispatchLocationView';
import ProofReceiptImageView from '@/views/transactions/dispatch/ProofReceiptImageView';
import ProofReceiptImageLocationView from '@/views/transactions/dispatch/ProofReceiptImageLocationView';
import DriversLicenseImageView from '@/views/transactions/dispatch/DriversLicenseImageView';

// Util
import { DateUtil } from '@/utils/dateutil';
import { DispatchUtil } from '@/utils/dispatchUtil';
import { DropDownItemsUtil } from '@/utils/dropDownItemsUtil';
import { LocationUtil } from '@/utils/locationUtil';
import { ValidationUtil } from '@/utils/validationUtil';
import { UserUtil } from '@/utils/userutil';

// API
import dispatchApi from '@/api/dispatchApi';

// DAO
import dispatchDAO from '@/database/dispatches';

// Others
import EventBus from '@/shared/event-bus';
import config from '@/config/env-constants';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import moment from 'moment';
import JsonExcel from 'vue-json-excel';
import truncate from 'vue-truncate-collapsed';
import _ from 'lodash';

export default {
	name: 'dispatch',
	components: {
		DispatchRowStatus,
		DispatchRowActions,
		DispatchDetailsView,
		DeployDispatch,
		CancelDispatch,
		ScannedAssetsView,
		PrintDispatchSummary,
		DispatchLocationView,
		ProofReceiptImageView,
		ProofReceiptImageLocationView,
		DriversLicenseImageView,
		Loading,
		JsonExcel,
		truncate
	},
	data() {
		return {
			items: [],
			fields: [
				{
					key: 'dispatchId',
					label: 'Dispatch ID',
					sortable: true,
				},
				{
					key: 'dateDeployed',
					sortable: true,
				},
				{
					key: 'dateReceived',
					sortable: true,
				},
				{
					key: 'source',
					sortable: true,
				},
				{
					key: 'destination',
					sortable: true,
				},
				{
					key: 'trucker',
					label: 'Trucker',
					sortable: true,
				},
				{
					key: 'Dispatched',
					label: 'Dispatched',
					sortable: true,
				},
				{
					key: 'Received',
					label: 'Received',
					sortable: true,
				},
				{
					key: 'status',
				},
				{
					key: 'actions',
					thClass: 'text-center'
				}
			],
			currentPage: 1,
			perPage: 10,
			totalRows: 0,
			pageOptions: [5, 10, 15, 25, 50, 100],
			sortBy: null,
			sortDesc: false,
			sortDirection: 'asc',
			filter: null,

			dateFormatOptions: { ...config.dateFormatOptions },

			defaultFilterBy: {
				dateFrom: moment().format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
				sourceCompany: { ...config.companyDefaultValue },
				destinationCompany: { ...config.companyDefaultValue },
				status: null,
				dispatchId: '',
				assetCode: '',
				plateNo: { ...config.transportationDefaultValue },
			},
			filterBy: {
				dateFrom: moment().format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
				sourceCompany: { ...config.companyDefaultValue },
				destinationCompany: { ...config.companyDefaultValue },
				status: null,
				dispatchId: '',
				assetCode: '',
				plateNo: { ...config.transportationDefaultValue },
			},
			prevFilterBy: {},

			params: {},

			allSourceCompanyOptions: [],
			allDestinationCompanyOptions: [],
			statusOptions: config.dispatchStatus,
			plateNoOptions: [],

			allCompaniesObj: {},
			allConnectedCompaniesObj: {},
			allStorageLocationsObj: {},
			allConnectionsObj: {},
			allUsersObj: {},
			allTransportationsObj: {},
			allAssetTypesObj: {},
			allDispatchesObj: {},

			isSuperAdmin: this.$store.getters.isSuperAdmin,
			isViewer: this.$store.getters.isViewer,
			isManager: this.$store.getters.isManager,
			isSupervisor: this.$store.getters.isSupervisor,
			loggedUserCompany: this.$store.getters.loggedUserCompany,
			loggedUser: this.$store.getters.loggedUser,
			// Check for loader
			isLoading: false,

			// Listener
			dispatchListener: null,
		};
	},
	watch: {
		"filterBy.sourceCompany": function (newVal) {
			if (!this.isSuperAdmin && newVal && newVal.id !== null) {
				let companyId = newVal.id;
				let parentCompanyId = newVal.parentCompanyId;

				if (companyId !== this.loggedUserCompany.id && parentCompanyId !== this.loggedUserCompany.id) {
					let companiesObj = _.filter(this.allCompaniesObj, o => {
						return o.id === this.loggedUserCompany.id || o.parentCompanyId === this.loggedUserCompany.id;
					});
					this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(companiesObj);
				} else {
					this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
				}
			} else {
				this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
			}
		},
		"filterBy.destinationCompany": function (newVal) {
			if (!this.isSuperAdmin && newVal && newVal.id !== null) {
				let companyId = newVal.id;
				let parentCompanyId = newVal.parentCompanyId;

				if (companyId !== this.loggedUserCompany.id && parentCompanyId !== this.loggedUserCompany.id) {
					let companiesObj = _.filter(this.allCompaniesObj, o => {
						return o.id === this.loggedUserCompany.id || o.parentCompanyId === this.loggedUserCompany.id;
					});
					this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(companiesObj);
				} else {
					this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
				}
			} else {
				this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
			}
		},
	},
	computed: {
		dateFrom() {
			const dateTo = moment();
			const dateFrom = dateTo.add(-30, 'days');
			return dateFrom.format('YYYY-MM-DD');
		},
		dateTo() {
			return moment().format('YYYY-MM-DD');
		},

		sourceIsRequired() {
			return !this.isSuperAdmin
		},

		/**
		 * Returns the set of data to be included in the export. For now this just
		 * returns the data as is.
		 *
		 * @returns {Array} the set of data to be included in the export.
		 */
		exportData() {
			return this.items;
		},

		/**
		 * Derives the field information based from the data table configuration.
		 *
		 * @returns {object} the fields to be included in the export.
		 */
		exportFields() {
			return {
				'Dispatch ID': 'dispatchId',
				'Date Deployed': 'Date Deployed',
				'Date Received': 'Date Received',
				Source: 'Source',
				Destination: 'Destination',
				Trucker: 'Trucker',
				'Dispatched': 'Dispatched',
				'Received': 'Received',
				Status: 'status',
				'Dispatched Assets': 'Dispatched Assets',
				'Received Assets': 'Received Assets',
				Remarks: 'notes',
				'Date Created': 'Date Created',
				'Date Updated': 'Date Updated',
				'Date Cancelled': 'Date Cancelled',
				'Created By': 'createdBy',
				'Deployed By': 'deployedBy',
				'Received By': 'receivedBy',
				'Updated By': 'updatedBy',
				'Cancelled By': 'cancelledBy',
			};
		},

		fileName() {
			let currTimeStamp = DateUtil.getCurrentTimestamp();
			return 'Dispatch-' + DateUtil.getDateInDDMMYYYYHHSSFormat(currTimeStamp);
		},
	},
	created() {
		this.$store.dispatch("fetchTransportationsIfNeeded", {
			currUserId: this.$store.getters.loggedUser.id,
			view: this.$store.getters.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY,
			filterBy: { companyId: this.$store.getters.loggedUserCompany.id },
		});
		this.$store.dispatch("fetchUsersIfNeeded", {
			currUserId: this.$store.getters.loggedUser.id,
			view: this.$store.getters.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY,
			filterBy: { companyId: this.$store.getters.loggedUserCompany.id, companyAccess: this.$store.getters.loggedUser.companyAccess },
		});
		this.$store.dispatch("fetchCompaniesIfNeeded", {
			currUserId: this.$store.getters.loggedUser.id,
			view: this.$store.getters.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY,
			filterBy: { companyId: this.$store.getters.loggedUserCompany.id, companyAccess: this.$store.getters.loggedUser.companyAccess, includeConnection: true },
		});
		this.$store.dispatch("fetchStorageLocationsIfNeeded", {
			currUserId: this.$store.getters.loggedUser.id,
			view: this.$store.getters.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY,
			filterBy: { companyId: this.$store.getters.loggedUserCompany.id, companyAccess: this.$store.getters.loggedUser.companyAccess, includeConnection: true },
		});
		this.$store.dispatch("fetchConnectionsIfNeeded", {
			currUserId: this.$store.getters.loggedUser.id,
			view: this.$store.getters.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY,
			filterBy: { companyId: this.$store.getters.loggedUserCompany.id, companyAccess: this.$store.getters.loggedUser.companyAccess },
		});
		this.$store.dispatch("fetchAssetTypesIfNeeded", {
			currUserId: this.$store.getters.loggedUser.id,
			view: this.$store.getters.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY,
			filterBy: { companyId: this.$store.getters.loggedUserCompany.id, companyAccess: this.$store.getters.loggedUser.companyAccess },
		});
	},

	mounted() {
		setTimeout(async () => {
			try {
				// Filter Access
				if (this.$store.getters.isScanner
					|| this.$store.getters.isAccounting
					|| this.$store.getters.isApprover
					|| this.$store.getters.isMaintenance) {
					this.$router.push('/dashboard');
					this.$toaster.warning('You are not allowed to access this page.');
				}

				// show loading indicator
				this.isLoading = true;

				await this.updateParams();

			} catch (_error) {
				this.$toaster.error('Error loading data. Please reload the page again.');
			} finally {
				// hide loading indicator
				this.isLoading = false;
			}
		}, config.timeout);

		// Event Listeners
		EventBus.$on('onCloseSaveDispatch', (dispatchObj) => {
			this.updateTable(dispatchObj);
		});
	},

	methods: {
		getValidationParam(isRequired, regex) {
			return {
				required: isRequired,
				regex: regex,
			};
		},
		listenerCallback(type, dispatch) {
			if ((!this.allDispatchesObj[dispatch.id] && type === "added") || type === "modified") {
				this.allDispatchesObj[dispatch.id] = dispatch;
				this.filterDispatches(this.allDispatchesObj);
			}
		},
		async updateParams() {
			// init parameters
			this.params = this.$store.getters.dispatchParams;

			if (!_.isEmpty(this.params) && this.hasCompleteParams(this.params)) {
				// reset filter to default
				this.filterBy = { ...this.params.filterBy };

				this.allCompaniesObj = this.params.allCompaniesObj;
				this.allConnectedCompaniesObj = this.params.allConnectedCompaniesObj;
				this.allStorageLocationsObj = this.params.allStorageLocationsObj;
				this.allConnectionsObj = this.params.allConnectionsObj;
				this.allUsersObj = this.params.allUsersObj;
				this.allTransportationsObj = this.params.allTransportationsObj;
				this.allAssetTypesObj = this.params.allAssetTypesObj;
				this.allDispatchesObj = this.params.allDispatchesObj;
			} else {
				// reset filter to default
				this.filterBy = { ...this.defaultFilterBy };

				this.allCompaniesObj = { ...this.$store.getters.companies };
				this.allConnectedCompaniesObj = this.isSuperAdmin ? { ...this.allCompaniesObj } : { ...this.$store.getters.connectedCompanies };
				this.allStorageLocationsObj = { ...this.$store.getters.storageLocations, ...this.$store.getters.connectedStorageLocations };
				this.allConnectionsObj = { ...this.$store.getters.connections };
				this.allUsersObj = { ...this.$store.getters.users, ...this.$store.getters.connectedUsers };
				this.allTransportationsObj = { ...this.$store.getters.transportations };
				this.allAssetTypesObj = { ...this.$store.getters.assetTypes };
			}

			// Create Source and Destination Company Options
			if (this.isSuperAdmin) {
				this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
				this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
			} else {
				this.allCompaniesObj = { ...this.allCompaniesObj, ...this.allConnectedCompaniesObj };

				this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
				this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allConnectedCompaniesObj);
			}

			// parse geoaddress of storage location
			_.forEach(this.allStorageLocationsObj, (value) => {
				value.geoaddress = LocationUtil.getGeoaddress(value.geoaddress);
			});

			// Create Transportations Options
			this.plateNoOptions = DropDownItemsUtil.retrievePlateNos(this.allTransportationsObj);


			// Set Default Filter Options
			if (!this.isSuperAdmin) {
				this.defaultFilterBy.sourceCompany = DropDownItemsUtil.getCompanyItem(this.loggedUserCompany);
			}
			this.defaultFilterBy.dateFrom = this.dateFrom;
			this.defaultFilterBy.dateTo = this.dateTo;

			await this.resetFilters();
			await this.retrieveData();
		},
		hasCompleteParams(params) {
			if (
				_.isEmpty(params.filterBy) ||
				_.isEmpty(params.allCompaniesObj) ||
				_.isEmpty(params.allConnectedCompaniesObj) ||
				_.isEmpty(params.allStorageLocationsObj) ||
				_.isEmpty(params.allConnectionsObj) ||
				_.isEmpty(params.allUsersObj) ||
				_.isEmpty(params.allTransportationsObj) ||
				_.isEmpty(params.allAssetTypesObj) ||
				_.isEmpty(params.allDispatchesObj)
			) {
				return false;
			}

			return params.fromAddDispatch || params.fromEditDispatch;
		},
		updateTable(dispatchObj) {
			if (_.isEmpty(dispatchObj)) {
				return;
			}
			this.allDispatchesObj[dispatchObj.id] = dispatchObj;
			this.filterDispatches(this.allDispatchesObj);
		},

		dateFromDisabled(_ymd, date) {
			return date > new Date();
		},
		isValidAssetCode(assetCode) {
			return _.isEmpty(assetCode) ||
				ValidationUtil.isValidAssetCode(this.allAssetTypesObj, assetCode);
		},
		validateFilter() {
			let isValid = true;

			if (_.isEmpty(this.filterBy.dateFrom) && _.isEmpty(this.filterBy.dateTo)) {
				this.$toaster.warning('Date From and Date To are required.');
				isValid = false;
			} else if ((_.isEmpty(this.filterBy.dateFrom) && !_.isEmpty(this.filterBy.dateTo)) ||
				(!_.isEmpty(this.filterBy.dateFrom) && _.isEmpty(this.filterBy.dateTo))) {
				this.$toaster.warning('Invalid Date Range. Date From and Date To must both have value.');
				isValid = false;
			} else if (this.filterBy.dateFrom > this.filterBy.dateTo) {
				this.$toaster.warning('Invalid Date Range. Date From must be less than Date To.');
				isValid = false;
			} else if (DateUtil.getNoOfDays(this.filterBy.dateFrom, this.filterBy.dateTo) > 90) {
				this.$toaster.warning('Invalid Date Range. Data range is allowed up to 90 days difference.');
				isValid = false;
			} else if (!this.isSuperAdmin && !this.filterBy.sourceCompany.id) {
				this.$toaster.warning('Source Company is required.');
				isValid = false;
			} else if (!this.isValidAssetCode(this.filterBy.assetCode)) {
				this.$toaster.warning(`Invalid Asset Code. "${this.filterBy.assetCode}" doesn't follow any of your asset tagging format.`);
				isValid = false;
			}

			return isValid;
		},

		async onFilterRequest() {
			let isValid = await this.$validator.validateAll();
			if (!isValid) {
				this.$toaster.warning('Please address the field/s with invalid input.');
				// hide loading indicator
				this.isLoading = false;
				return;
			}

			if (!this.validateFilter()) {
				return;
			}

			if (!_.isEqual(this.filterBy, this.prevFilter)) {
				await this.retrieveData();
				this.prevFilter = { ...this.filterBy };
			}
		},
		async resetFilters() {
			if (!_.isEqual(this.filterBy, this.defaultFilterBy)) {
				// reset to default
				this.filterBy = { ...this.defaultFilterBy };
				this.prevFilter = { ...this.filterBy };

				// reset validation
				this.$validator.reset();
				this.errors.clear();

				await this.retrieveData();
			}
		},
		getFilterParam() {
			let filter = { ...this.filterBy };
			filter.source = 'Dispatch';
			filter.fromTimestamp = DateUtil.startDateTimeStamp(new Date(filter.dateFrom));
			filter.toTimestamp = DateUtil.endDateTimeStamp(new Date(filter.dateTo));
			filter.companyId = this.loggedUserCompany.id;
			return filter;
		},
		async retrieveData() {
			try {
				// show loading indicator
				this.isLoading = true;

				let filter = this.getFilterParam();

				if (this.isSuperAdmin) {
					const { data } = await dispatchApi.getDispatches(
						filter,
						config.view.ADMIN,
						this.loggedUser.id
					);
					this.allDispatchesObj = data.dispatches;
				} else {
					const { data } = await dispatchApi.getDispatches(
						filter,
						config.view.COMPANY,
						this.loggedUser.id
					);
					this.allDispatchesObj = data.dispatches;
				}

				this.filterDispatches(this.allDispatchesObj);

				// update listener
				this.dispatchListener = dispatchDAO.getDispatchListener(filter, this.listenerCallback);
			} catch (_error) {
				this.$toaster.error('Error loading data. Please reload the page again.');
			} finally {
				// hide loading indicator
				this.isLoading = false;
			}
		},
		filterDispatches(allDispatchesObj) {
			let filteredObjs = { ...allDispatchesObj };

			let fromTimestamp = DateUtil.startDateTimeStamp(new Date(this.filterBy.dateFrom));
			let toTimestamp = DateUtil.endDateTimeStamp(new Date(this.filterBy.dateTo));

			_.forEach(allDispatchesObj, (dispatch, id) => {
				this.filterBySourceAndDestination(filteredObjs, dispatch, id);

				if (dispatch.dateCreated < fromTimestamp && dispatch.dateCreated > toTimestamp) {
					delete filteredObjs[id];
				}

				let sourceCompanyId = this.filterBy.sourceCompany.id;
				if (sourceCompanyId && sourceCompanyId.length > 0 && dispatch.source.companyId !== sourceCompanyId) {
					delete filteredObjs[id];
				}

				let destinationCompanyId = this.filterBy.destinationCompany.id;
				if (destinationCompanyId && destinationCompanyId.length > 0 && dispatch.destination.companyId !== destinationCompanyId) {
					delete filteredObjs[id];
				}

				let status = this.filterBy.status;
				if (status && status.length > 0 && status !== dispatch.status) {
					delete filteredObjs[id];
				}

				let dispatchId = this.filterBy.dispatchId;
				if (dispatchId && dispatchId.length > 0 && dispatchId !== dispatch.dispatchId) {
					delete filteredObjs[id];
				}

				let plateNo = this.filterBy.plateNo;
				if (plateNo && plateNo.length > 0 && plateNo !== dispatch.transportation.plateNo) {
					delete filteredObjs[id];
				}

				let assetCode = this.filterBy.assetCode;
				if (assetCode && assetCode.length > 0 && !dispatch.assets.includes(assetCode)) {
					delete filteredObjs[id];
				}
			});

			this.processDispatches(filteredObjs);
		},
		filterBySourceAndDestination(filteredObjs, dispatch, id) {
			let sourceCompanyId = dispatch.source.companyId;
			let destinationCompanyId = dispatch.destination.companyId;

			let companiesObj;
			if (this.isSuperAdmin) {
				companiesObj = { ...this.allCompaniesObj };
			} else {
				companiesObj = _.filter(this.allCompaniesObj, o => {
					return o.id === this.loggedUserCompany.id || o.parentCompanyId === this.loggedUserCompany.id;
				});
			}

			if (!UserUtil.hasCompanyAccess(companiesObj, sourceCompanyId)
				&& !UserUtil.hasCompanyAccess(companiesObj, destinationCompanyId)) {
				delete filteredObjs[id];
			}
		},
		processDispatches(dispatches) {
			this.items = Object.values(dispatches);

			this.items.forEach((item) => {
				// Parse source and destination display
				item['Source'] = DispatchUtil.getCompanyLocationDisplay(item.source);
				item['Destination'] = DispatchUtil.getCompanyLocationDisplay(item.destination);

				// Transportation
				item['Trucker'] = item.transportation.plateNo + ' (' + item.driver.name + ')';
				item['Truck Assistant'] = item.driver.assistants;

				// Parse deployed assets
				item['Dispatched Assets'] = DispatchUtil.getDispatchedAssets(item);
				item['Dispatched'] = item['Dispatched Assets'].length;

				// Parse received assets
				item['Received Assets'] = DispatchUtil.getReceivedAssets(item);
				item['Received'] = item['Received Assets'].length;

				// Parse timestamps
				item['Date Created'] = this.getDisplayDateTime(item.dateCreated);
				item['Date Updated'] = this.getDisplayDateTime(item.dateUpdated);
				item['Date Deployed'] = this.getDisplayDateTime(item.dateDeployed);
				item['Date Received'] = this.getDisplayDateTime(item.dateReceived);
				item['Date Cancelled'] = this.getDisplayDateTime(item.dateCancelled);

				// remove show details
				delete item._showDetails;
			});
			this.items = _.sortBy(this.items, ['dateCreated']);
			this.items.reverse();
			this.totalRows = this.items.length;

			this.$store.dispatch('updateAllDispatches', this.allDispatchesObj);

			// refresh table
			if (this.$refs.dispatchesTable) {
				this.$refs.dispatchesTable.refresh();
			}

			this.updateDispatchParam();
		},

		addDispatch() {
			this.updateDispatchParam();
			this.$store.dispatch('setCurrentDispatch', {});

			// redirect to Add Dispatch page
			if (this.isSuperAdmin) {
				this.$router.push({ path: '/admin/admin-add-dispatch' });
			} else {
				this.$router.push({ path: '/add-dispatch' });
			}
		},
		updateDispatchParam() {
			this.params = {
				allCompaniesObj: this.allCompaniesObj,
				allConnectedCompaniesObj: this.allConnectedCompaniesObj,
				allStorageLocationsObj: this.allStorageLocationsObj,
				allConnectionsObj: this.allConnectionsObj,
				allUsersObj: this.allUsersObj,
				allTransportationsObj: this.allTransportationsObj,
				allAssetTypesObj: this.allAssetTypesObj,
				allDispatchesObj: this.allDispatchesObj,
				filterBy: this.filterBy,
			};

			this.$store.dispatch('setDispatchParams', this.params);
		},

		// UTILS
		getDisplayDateTime(date) {
			if (date) {
				return DateUtil.getFormattedDateWithTime(date);
			}
			return "-";
		},
		breakRemarks(remarks, length) {
			return remarks.length > length ? remarks.replace(new RegExp(`([^\\s]{${length}})`, 'g'), '$1<br>') : remarks;
		}
	},

	beforeUnmount() {
		if (this.dispatchListener != null) {
			// Stop listening to changes
			this.dispatchListener();
		}
	},

	beforeDestroy() {
		EventBus.$off('onCloseSaveDispatch');
	},
};
</script>